import apiClient from './api';
import { Agent, ApiResponse, AgentStats, DashboardStats } from '@/types';

class AgentsService {
  async getAgents(): Promise<Agent[]> {
    const response = await apiClient.get<ApiResponse<Agent[]>>('/agents');
    return response.data.data || [];
  }

  async getAgent(id: string): Promise<Agent> {
    const response = await apiClient.get<ApiResponse<Agent>>(`/agents/${id}`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Agent not found');
  }

  async createAgent(data: any): Promise<Agent> {
    const response = await apiClient.post<ApiResponse<Agent>>('/agents', data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to create agent');
  }

  async updateAgent(id: string, data: Partial<Agent>): Promise<Agent> {
    const response = await apiClient.put<ApiResponse<Agent>>(`/agents/${id}`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update agent');
  }

  async connectWorkflow(id: string, data: { workflow_id: string; name?: string }): Promise<Agent> {
    const response = await apiClient.put<ApiResponse<Agent>>(`/agents/${id}/workflow`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to connect workflow');
  }

  async updateWorkflowConfig(
    id: string,
    data: { credential_bindings: Record<string, { integration_id: string }> },
  ): Promise<Agent> {
    const response = await apiClient.put<ApiResponse<Agent>>(`/agents/${id}/workflow/config`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update workflow config');
  }

  async disconnectWorkflow(id: string): Promise<Agent> {
    const response = await apiClient.delete<ApiResponse<Agent>>(`/agents/${id}/workflow`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to disconnect workflow');
  }

  async updateAgentStatus(id: string, status: string): Promise<Agent> {
    const response = await apiClient.post<ApiResponse<Agent>>(`/agents/${id}/status`, { status });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update agent status');
  }

  async deleteAgent(id: string): Promise<void> {
    await apiClient.delete(`/agents/${id}`);
  }

  async getAgentStats(id: string): Promise<AgentStats> {
    const response = await apiClient.get<ApiResponse<AgentStats>>(`/agents/${id}/stats`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get agent stats');
  }

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get<ApiResponse<DashboardStats>>('/dashboard/stats');
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get dashboard stats');
  }

  async getLatestInsight(): Promise<any | null> {
    const response = await apiClient.get<ApiResponse<any>>('/dashboard/insights/latest');
    return response.data.data || null;
  }

  async generateInsight(): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>('/dashboard/insights/generate');
    return response.data.data;
  }

  async markInsightViewed(id: string): Promise<void> {
    await apiClient.patch(`/dashboard/insights/${id}/viewed`);
  }

  async testAgent(
    agentId: string,
    payload: {
      message: string;
      history: Array<{ content: string; sender_type: string; content_type: string }>;
      collected_data: Record<string, unknown>;
      session_id?: string;
      attachments?: Array<{ filename: string; type: string; size: number }>;
    }
  ): Promise<{
    message: string;
    confidence: number;
    suggested_status?: string;
    collected_data?: Record<string, unknown>;
    extracted_fields?: string[];
    knowledge_context_used?: boolean;
    conversation_id?: string;
    session_id?: string;
    pending_extraction?: boolean;
  }> {
    const response = await apiClient.post<
      ApiResponse<{
        message: string;
        confidence: number;
        suggested_status?: string;
        collected_data?: Record<string, unknown>;
        extracted_fields?: string[];
        knowledge_context_used?: boolean;
        conversation_id?: string;
        session_id?: string;
        pending_extraction?: boolean;
      }>
    >(`/agents/${agentId}/test`, payload);

    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to test agent');
  }

  async getTestSessions(agentId: string): Promise<Array<{
    conversation_id: string;
    session_id: string;
    status: string;
    message_count: number;
    last_message_at: string | null;
    created_at: string;
  }>> {
    const response = await apiClient.get<ApiResponse<Array<{
      conversation_id: string;
      session_id: string;
      status: string;
      message_count: number;
      last_message_at: string | null;
      created_at: string;
    }>>>(`/agents/${agentId}/test-sessions`);
    return response.data.data || [];
  }

  async getConversationMessages(conversationId: string): Promise<Array<{
    id: string;
    role: string;
    content: string;
    sender_type: string;
    created_at: string;
  }>> {
    const response = await apiClient.get<ApiResponse<Array<{
      id: string;
      role: string;
      content: string;
      sender_type: string;
      created_at: string;
    }>>>(`/conversations/${conversationId}/messages`);
    return response.data.data || [];
  }
}

export const agentsService = new AgentsService();
export default agentsService;
